import { __rest, __assign } from 'tslib';
import * as React from 'react';
import { UitkLayoutSpaces, UitkLayout, UitkLayoutSizes } from 'uitk-react-layout';
import { classNames } from 'uitk-react-utils';
import styles from './uitk-layout-flex.css';

var EgdsSizingLengths = [
    '0x',
    '0x__half',
    '0x__quarter',
    '1x',
    '1x__half',
    '2x',
    '3x',
    '3x__half',
    '4x',
    '4x__half',
    '5x',
    '6x',
    '7x',
    '8x',
    '9x',
    '10x',
    '11x',
    '12x',
    '13x',
    '14x',
    '15x',
    '16x',
    '17x',
    '18x',
    '24x',
    '30x',
    '37x',
    '38x',
    '40x',
    '48x',
    '70x',
    '72x',
    '76x',
    '88x',
    '96x',
    '104x',
    '112x',
    '120x',
    '180x',
    '312x',
    '500x'
];
var UitkLayoutFlexDirectionMap = [
    ['row', 'flex-direction-row'],
    ['row-reverse', 'flex-direction-row-reverse'],
    ['column', 'flex-direction-column'],
    ['column-reverse', 'flex-direction-column-reverse']
];
var UitkLayoutFlexDirections = new Map(UitkLayoutFlexDirectionMap);
var UitkLayoutFlexWrapMap = [
    ['nowrap', 'flex-wrap-nowrap'],
    ['wrap', 'flex-wrap-wrap'],
    ['wrap-reverse', 'flex-wrap-wrap-reverse']
];
var UitkLayoutFlexWraps = new Map(UitkLayoutFlexWrapMap);
var UitkLayoutFlexAlignContentMap = [
    ['center', 'align-content-center'],
    ['end', 'align-content-flex-end'],
    ['start', 'align-content-flex-start'],
    ['space-around', 'align-content-space-around'],
    ['space-between', 'align-content-space-between']
];
var UitkLayoutFlexAlignContents = new Map(UitkLayoutFlexAlignContentMap);
var UitkLayoutFlexJustifyContentMap = [
    ['center', 'justify-content-center'],
    ['end', 'justify-content-flex-end'],
    ['start', 'justify-content-flex-start'],
    ['space-around', 'justify-content-space-around'],
    ['space-between', 'justify-content-space-between']
];
var UitkLayoutFlexJustifyContents = new Map(UitkLayoutFlexJustifyContentMap);
var UitkLayoutFlexAlignItemsMap = [
    ['center', 'align-items-center'],
    ['end', 'align-items-flex-end'],
    ['start', 'align-items-flex-start'],
    ['stretch', 'align-items-stretch']
];
var UitkLayoutFlexAlignItems = new Map(UitkLayoutFlexAlignItemsMap);
var UitkLayoutFlexItemAlignSelfMap = [
    ['center', 'align-self-center'],
    ['end', 'align-self-flex-end'],
    ['start', 'align-self-flex-start'],
    ['stretch', 'align-self-stretch']
];
var UitkLayoutFlexItemAlignSelfs = new Map(UitkLayoutFlexItemAlignSelfMap);
var UitkLayoutFlexBlockSizeMap = [['full_size', 'block-size-full-size']];
var UitkLayoutFlexBlockSizes = new Map(UitkLayoutFlexBlockSizeMap);

var getClassNameList$1 = classNames.bind(styles);
var UitkLayoutFlex = function (props) {
    var alignContent = props.alignContent, alignItems = props.alignItems, blockSize = props.blockSize, children = props.children, className = props.className, direction = props.direction, justifyContent = props.justifyContent, space = props.space, tag = props.tag, domRef = props.domRef, wrap = props.wrap, dataTestId = props.dataTestId, attributes = __rest(props, ["alignContent", "alignItems", "blockSize", "children", "className", "direction", "justifyContent", "space", "tag", "domRef", "wrap", "dataTestId"]);
    var baseClass = 'uitk-layout-flex';
    var wrapperClassNames = getClassNameList$1(baseClass, alignContent && baseClass + "-" + UitkLayoutFlexAlignContents.get(alignContent), alignItems && baseClass + "-" + UitkLayoutFlexAlignItems.get(alignItems), blockSize && baseClass + "-" + UitkLayoutFlexBlockSizes.get(blockSize), direction && baseClass + "-" + UitkLayoutFlexDirections.get(direction), justifyContent && baseClass + "-" + UitkLayoutFlexJustifyContents.get(justifyContent), space && baseClass + "-" + UitkLayoutSpaces.get(space), wrap && baseClass + "-" + UitkLayoutFlexWraps.get(wrap), className);
    return children ? (React.createElement(UitkLayout, __assign({ className: wrapperClassNames, tag: tag, domRef: domRef }, attributes, (dataTestId && { 'data-test-id': dataTestId })), children)) : null;
};
UitkLayoutFlex.displayName = 'UitkLayoutFlex';

var getClassNameList = classNames.bind(styles);
var UitkLayoutFlexItem = function (props) {
    var alignSelf = props.alignSelf, className = props.className, maxWidth = props.maxWidth, minWidth = props.minWidth, grow = props.grow, shrink = props.shrink, style = props.style;
    /**
     * Check string is a valid EgdsSizingLength value
     *
     * @param size {string} - size
     * @returns EgdsSizingLengths.includes(size as EgdsSizingLength)
     */
    var IsEgdsSize = function (size) { return EgdsSizingLengths.includes(size); };
    var childrenArray = React.Children.toArray(props.children);
    if (!childrenArray.length) {
        return null;
    }
    var firstChild = childrenArray[0];
    var firstChildClassName = firstChild.props.className;
    var firstChildStyle = firstChild.props.style;
    var baseClass = 'uitk-layout-flex-item';
    /**
     * Return a valid className for minWidth or maxWidth
     *
     * @param prop {typeof minWidth | typeof maxWidth} - prop
     * @param prefix {"flex-basis" | "max-width"} - className prefix
     * @returns string
     */
    var lengthClassName = function (prop, prefix) {
        return prop && typeof prop === 'string' && baseClass + "-" + prefix + "-" + (IsEgdsSize(prop) ? prop : UitkLayoutSizes.get(prop));
    };
    var wrapperClassNames = getClassNameList(alignSelf && baseClass + "-" + UitkLayoutFlexItemAlignSelfs.get(alignSelf), baseClass, firstChildClassName, lengthClassName(maxWidth, 'max-width'), lengthClassName(minWidth, 'flex-basis'), typeof grow !== 'undefined' && Number.isInteger(grow) && baseClass + "-flex-grow-" + grow, typeof shrink !== 'undefined' && Number.isInteger(shrink) && baseClass + "-flex-shrink-" + shrink, className);
    var wrapperStyle = __assign(__assign(__assign(__assign({}, firstChildStyle), style), (minWidth &&
        typeof minWidth === 'number' && {
        flexBasis: minWidth + "px"
    })), (maxWidth &&
        typeof maxWidth === 'number' && {
        maxInlineSize: maxWidth + "px"
    }));
    var decoratedChild = React.cloneElement(firstChild, __assign({ className: wrapperClassNames }, (Object.keys(wrapperStyle).length && {
        style: wrapperStyle
    })));
    return decoratedChild;
};
UitkLayoutFlexItem.displayName = 'UitkLayoutFlexItem';

export { UitkLayoutFlex, UitkLayoutFlexItem };
